<template>
  <div class="live-consult" data-test-id="LiveConsultComponent">
    <div id="local-video"></div>
    <div id="remote-video"></div>
    <div v-if="!participants || participants.length === 0" class="waiting-others-join">
      <span>Waiting for others to join...</span>
    </div>
    <div class="controls-container">
      <div class="control-item-container">
        <div class="control-item" :class="{ red: !isMicEnabled }" @click="toggleMic">
          <b-icon v-if="isMicEnabled" icon="mic-fill" font-scale="2"></b-icon>
          <b-icon v-if="!isMicEnabled" icon="mic-mute-fill" font-scale="2"></b-icon>
        </div>
      </div>
      <div class="control-item-container">
        <div class="control-item red" @click="endCall">
          <b-icon icon="telephone-fill" font-scale="2"></b-icon>
        </div>
      </div>
      <div class="control-item-container">
        <div class="control-item" :class="{ red: !isVideoEnabled }" @click="toggleVideo">
          <b-icon v-if="isVideoEnabled" icon="camera-video-fill" font-scale="2"></b-icon>
          <b-icon v-if="!isVideoEnabled" icon="camera-video-off-fill" font-scale="2"></b-icon>
        </div>
      </div>
      <div v-if="cameras && cameras.length > 1" class="control-item-container">
        <div class="control-item" @click="switchCamera">
          <b-icon icon="arrow-repeat" font-scale="2"></b-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { connect, createLocalTracks, createLocalVideoTrack } from "twilio-video";
import { BIcon } from "bootstrap-vue";
import { CaseEvent, CaseUpdateEventType } from "@/Case/CaseEvent";

export default {
  name: "LiveConsult",
  components: { BIcon },
  props: {},
  data() {
    return {
      room: undefined,
      participants: [],
      tracks: undefined,
      videoTrack: undefined,
      isMicEnabled: true,
      isVideoEnabled: true,
      cameras: [],
      selectedCamera: "user",
      selectedCameraIndex: undefined,
    };
  },
  async mounted() {
    await this.checkCameras();
    this.startLiveConsult();
  },
  destroyed() {
    if (this.room) {
      this.room.disconnect();
    }
  },
  methods: {
    ...mapActions("Case", ["getLiveConsultRoom"]),
    async startLiveConsult() {
      const data = await this.getLiveConsultRoom();

      if (this.cameras && this.cameras.length) {
        this.selectedCameraIndex = 0;
        this.selectedCamera = this.cameras[this.selectedCameraIndex];
      }

      this.tracks = await createLocalTracks({
        audio: true,
        video: {
          deviceId: this.selectedCamera,
        },
      });

      // Display camera preview.
      this.videoTrack = this.tracks.find((track) => track.kind === "video");
      const divContainer = document.getElementById("local-video");
      if (divContainer) {
        const videoElement = this.videoTrack.attach();
        divContainer.appendChild(videoElement);
      }

      // Join the Room with the pre-acquired LocalTracks.
      const room = await connect(data.authToken, {
        name: data.roomId,
        tracks: this.tracks,
      });

      this.room = room;

      this.room.participants.forEach(this.onParticipantConnect);
      this.room.on("participantConnected", this.onParticipantConnect);
      this.room.on("participantDisconnected", this.onParticipantDisconnect);
      this.room.once("disconnected", this.onDisconnect);

      this.$emit(CaseEvent.MODIFY, {
        eventType: CaseUpdateEventType.liveConsult,
        data: {
          liveConsult: {
            started: true,
            minimized: false,
          },
        },
      });
    },
    toggleMic() {
      this.isMicEnabled = !this.isMicEnabled;
      this.room.localParticipant.audioTracks.forEach((audioTrack) => {
        this.isMicEnabled ? audioTrack.track.enable() : audioTrack.track.disable();
      });
    },
    toggleVideo() {
      this.isVideoEnabled = !this.isVideoEnabled;
      this.room.localParticipant.videoTracks.forEach((videoTrack) => {
        this.isVideoEnabled ? videoTrack.track.enable() : videoTrack.track.disable();
      });
    },
    endCall() {
      if (this.room) {
        this.room.disconnect();
      }
      this.$emit("hide-modal");
    },
    async checkCameras() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      if (devices && devices.length) {
        devices.forEach((device) => {
          if (device.kind === "videoinput") {
            this.cameras.push(device.deviceId);
          }
        });
      }
    },
    async switchCamera() {
      if (this.cameras && this.cameras.length) {
        this.selectedCameraIndex += 1;
        if (this.selectedCameraIndex >= this.cameras.length) {
          this.selectedCameraIndex = 0;
        }
        this.selectedCamera = this.cameras[this.selectedCameraIndex];
      }

      this.videoTrack.stop();
      this.room.localParticipant.unpublishTrack(this.videoTrack);
      this.videoTrack = undefined;

      const localVideoTrack = await createLocalVideoTrack({
        deviceId: this.selectedCamera,
      });

      this.room.localParticipant.publishTrack(localVideoTrack);

      this.videoTrack = localVideoTrack;

      const divContainer = document.getElementById("local-video");
      if (divContainer) {
        while (divContainer.firstChild) {
          divContainer.removeChild(divContainer.firstChild);
        }
        const videoElement = this.videoTrack.attach();
        divContainer.appendChild(videoElement);
      }
    },
    onParticipantConnect(participant) {
      this.participants.push(participant);

      const div = document.createElement("div");
      div.id = participant.sid;

      participant.on("trackSubscribed", (track) => this.trackSubscribed(div, track));
      participant.on("trackUnsubscribed", this.trackUnsubscribed);

      participant.tracks.forEach((publication) => {
        if (publication.isSubscribed) {
          this.trackSubscribed(div, publication.track);
        }
      });
    },
    onParticipantDisconnect(participant) {
      const div = document.getElementById(participant.sid);
      if (div) {
        div.remove();
      }

      const elementExistsAt = this.participants.findIndex((p) => p.sid === participant.sid);
      if (elementExistsAt > -1) {
        this.participants.splice(elementExistsAt, 1);
      }
    },
    trackSubscribed(div, track) {
      const divContainer = document.getElementById("remote-video");
      if (divContainer) {
        div.appendChild(track.attach());
        divContainer.appendChild(div);
      }
    },
    trackUnsubscribed(track) {
      track.detach().forEach((element) => {
        element.remove();
      });
    },
    onDisconnect() {
      if (this.tracks) {
        this.tracks.forEach((track) => {
          track.stop();
        });
        this.tracks = undefined;
        this.videoTrack = undefined;
      }

      const parentElement = document.getElementById("remote-video");
      if (parentElement) {
        while (parentElement.firstChild) {
          parentElement.removeChild(parentElement.firstChild);
        }
      }

      this.room = undefined;

      this.$emit(CaseEvent.MODIFY, {
        eventType: CaseUpdateEventType.liveConsult,
        data: {
          liveConsult: {
            started: false,
            minimized: false,
          },
        },
      });
    },
  },
};
</script>

<style lang="scss">
.live-consult {
  #local-video {
    max-width: 60px;
    max-height: 47px;
    position: absolute;
    right: 25px;
    top: 25px;

    video {
      width: 60px;
      border-radius: 4px;
      border: 1px solid #000;
    }
  }

  #remote-video {
    video {
      width: 100%;
      border-radius: 4px;
    }
  }

  .waiting-others-join {
    height: 350px;
    background-color: #efefef;
    border-radius: 4px;
    color: #000;
    display: flex;
    align-items: center;

    span {
      margin: 0 auto;
      background-color: #d4d4d4;
      padding: 10px;
      border-radius: 4px;
    }
  }

  .controls-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 15px;

    .control-item-container {
      flex: 1 1;

      .control-item {
        background-color: #fff;
        color: #000;
        border: 1px solid #000;
        border-radius: 50%;
        width: 50px;
        height: 50px;
        margin: 0 auto;
        padding: 8px;
        cursor: pointer;

        &.red {
          background-color: #c00000;
          color: #fff;
          border: 1px solid #c00000;
        }
      }
    }
  }
}
</style>
